import { useRef, useState } from 'react';
import {
    Button,
    Card,
    DateTimePicker,
    EmptyState,
    EntitySearchSelect,
    ExpandableDataTable,
    FormNumberInput,
    SegmentedControl
} from '@/components/UI';
import { DataLoadingSpinner } from '@/components/UI/DataLoadingSpinner';
import { trpcClient } from '@/api/trpc';
import { showToast } from '@/components/Layout/Toast';
import type { EntryScorecardsRow } from '@/types/appRouter';
import type { EntityTypeUnion } from '@einhaus/project314-types';
import { getResearchWindow } from '@einhaus/project314-shared';
import { OPTIONS_DATA_START_DATETIME } from '@/config/constants';
import PreviewDiagnostics from './PreviewDiagnostics';
import PreviewEntryDetails from './PreviewEntryDetails';
import { getNullableNumberSortValue, getPreviewRuleRoleCounts } from './helpers';
import type { ScorecardFeatureRow, ScorecardPreviewDiagnostics, ScorecardPreviewRow, StrategyContext, ThresholdsMeta } from './types';

interface PreviewCardProps {
    scorecard: EntryScorecardsRow;
    strategyContext: StrategyContext | null;
    minPredictedProfit: number;
    thresholds: ThresholdsMeta;
    registryFeatureByKey: Map<string, ScorecardFeatureRow>;
}

type PreviewResearchWindowPreset = 'training' | 'walkforward';
type PreviewResearchWindowMode = PreviewResearchWindowPreset | 'custom';

const buildPreviewDateWindow = (mode: PreviewResearchWindowPreset): { start: string; end: string } => {
    const researchWindow = getResearchWindow(OPTIONS_DATA_START_DATETIME, new Date());

    return mode === 'training'
        ? { start: researchWindow.trainingStart, end: researchWindow.trainingEnd }
        : { start: researchWindow.walkForwardStart, end: researchWindow.walkForwardEnd };
};

const PreviewCard = ({ scorecard, strategyContext, minPredictedProfit, thresholds, registryFeatureByKey }: PreviewCardProps) => {
    // Preview (entries-based)
    const [selectedEntity, setSelectedEntity] = useState<{
        entityId: number;
        entityType: EntityTypeUnion['entityType'];
        symbol?: string;
        name?: string;
    } | null>(null);

    const initialPreviewWindow = buildPreviewDateWindow('walkforward');
    const [startDateTime, setStartDateTime] = useState<string>(initialPreviewWindow.start);
    const [endDateTime, setEndDateTime] = useState<string>(initialPreviewWindow.end);
    const [previewWindowMode, setPreviewWindowMode] = useState<PreviewResearchWindowMode>('walkforward');
    const [includeRejected, setIncludeRejected] = useState<boolean>(false);
    const [limit, setLimit] = useState<number | null>(50);

    const [previewRows, setPreviewRows] = useState<ScorecardPreviewRow[]>([]);

    const [diagnostics, setDiagnostics] = useState<ScorecardPreviewDiagnostics | null>(null);

    const [previewPending, setPreviewPending] = useState(false);

    // Entity the currently displayed results belong to, so stale rows are never attributed to a newly selected entity.
    const [previewedEntityLabel, setPreviewedEntityLabel] = useState<string | null>(null);

    // Guards against an earlier, slower preview response overwriting the results of a newer one.
    const previewRequestIdRef = useRef(0);

    const clearPreviewResults = () => {
        setPreviewRows([]);
        setDiagnostics(null);
        setPreviewedEntityLabel(null);
    };

    const applyPreviewDateWindow = (mode: PreviewResearchWindowPreset) => {
        const window = buildPreviewDateWindow(mode);
        setPreviewWindowMode(mode);
        setStartDateTime(window.start);
        setEndDateTime(window.end);
    };

    const doPreview = async (override?: { start: string; end: string }) => {
        if (!scorecard) return;
        if (!selectedEntity) return showToast('Select an entity to preview', 'error');

        const strategyIdForPreview = strategyContext?.strategyId;
        if (!strategyIdForPreview) return showToast('Missing strategy for preview', 'error');

        if (limit !== null && limit <= 0) return showToast('Limit must be a positive number', 'error');

        const requestId = previewRequestIdRef.current + 1;
        previewRequestIdRef.current = requestId;

        clearPreviewResults();
        setPreviewPending(true);

        const entityLabel = selectedEntity.symbol ?? `${selectedEntity.entityType} #${selectedEntity.entityId}`;

        try {
            const resp = await trpcClient.scorecards.previewEntries.query({
                scorecardId: scorecard.id,
                strategyId: strategyIdForPreview,
                entityId: selectedEntity.entityId,
                entityType: selectedEntity.entityType,
                startDateTime: override?.start ?? startDateTime,
                endDateTime: override?.end ?? endDateTime,
                includeRejected,
                debug: true,
                ...(limit !== null ? { limit } : {})
            });

            if (previewRequestIdRef.current !== requestId) return;

            if (Array.isArray(resp)) {
                // Backward compatibility (should not happen after type refresh)
                setPreviewRows(resp);
                setDiagnostics(null);
            } else {
                setPreviewRows(resp.results);
                setDiagnostics(resp.diagnostics ?? null);
            }

            setPreviewedEntityLabel(entityLabel);
        } catch (e: unknown) {
            if (previewRequestIdRef.current !== requestId) return;

            clearPreviewResults();
            showToast((e as Error)?.message || 'Preview failed', 'error');
        } finally {
            if (previewRequestIdRef.current === requestId) setPreviewPending(false);
        }
    };

    return (
        <Card className="p-4">
            <h3 className="text-white text-lg font-semibold mb-3">Preview</h3>
            <div className="space-y-4">
                <div className="space-y-3">
                    <EntitySearchSelect
                        label="Entity"
                        value={selectedEntity}
                        onChange={(e) => {
                            // Results belong to the previously selected entity, so drop them as soon as the entity changes.
                            previewRequestIdRef.current += 1;
                            setPreviewPending(false);
                            clearPreviewResults();
                            setSelectedEntity(
                                e
                                    ? {
                                          entityId: e.entityId,
                                          entityType: e.entityType,
                                          symbol: e.symbol,
                                          name: e.name
                                      }
                                    : null
                            );
                        }}
                    />
                    {selectedEntity ? (
                        <div className="text-sm text-zinc-300">
                            Selected: <span className="text-white font-medium">{selectedEntity.symbol}</span>{' '}
                            <span className="text-zinc-400">{selectedEntity.name}</span>{' '}
                            <span className="text-zinc-500">
                                ({selectedEntity.entityType} #{selectedEntity.entityId})
                            </span>
                        </div>
                    ) : (
                        <EmptyState size="sm" title="No entity selected" />
                    )}
                    <DateTimePicker
                        autoApply
                        endDateTime={endDateTime}
                        startDateTime={startDateTime}
                        onDateRangeChange={(s, e) => {
                            setPreviewWindowMode('custom');
                            setStartDateTime(s);
                            setEndDateTime(e);
                            // No immediate preview; Preview Scoring button will honor latest values
                        }}
                    />
                    <div>
                        <div className="mb-2 text-sm text-zinc-300">Research window</div>
                        <SegmentedControl<PreviewResearchWindowMode>
                            options={[
                                { value: 'training', label: 'Training' },
                                { value: 'walkforward', label: 'Walk-forward' },
                                { value: 'custom', label: 'Custom' }
                            ]}
                            value={previewWindowMode}
                            onChange={(mode) => {
                                if (mode === 'custom') {
                                    setPreviewWindowMode('custom');
                                    return;
                                }

                                applyPreviewDateWindow(mode);
                            }}
                        />
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                        <label className="text-sm text-zinc-300 flex items-center gap-2">
                            <input
                                checked={includeRejected}
                                className="cursor-pointer"
                                type="checkbox"
                                onChange={(e) => setIncludeRejected(e.target.checked)}
                            />
                            Include rejected
                        </label>
                        <FormNumberInput integer label="Limit" min={1} value={limit} onValueChange={setLimit} />
                    </div>
                    <div className="mt-2 flex gap-2">
                        <Button disabled={previewPending} variant="primary" onClick={() => doPreview()}>
                            {previewPending ? 'Fetching…' : 'Preview scoring'}
                        </Button>
                    </div>
                </div>
                <div className="space-y-3 w-full">
                    {previewPending && <DataLoadingSpinner text="Running preview…" />}
                    {!previewPending && diagnostics && <PreviewDiagnostics diagnostics={diagnostics} />}
                    {!previewPending && previewRows.length > 0 ? (
                        <div className="space-y-2">
                            {/* Entry summary */}
                            <div className="text-sm text-zinc-300">
                                {previewedEntityLabel && (
                                    <div className="mb-1 text-xs text-zinc-400">
                                        Results for <span className="text-white font-medium">{previewedEntityLabel}</span>
                                    </div>
                                )}
                                <div>
                                    Min predicted profit: <span className="text-white font-medium">{minPredictedProfit}%</span>
                                    {' • '}Min matched rules: <span className="text-white font-medium">{thresholds.minMatchedRules}</span>
                                    {' • '}Min positive boost matches:{' '}
                                    <span className="text-white font-medium">{thresholds.minPositiveMatchedRules}</span>
                                </div>
                                <div className="mt-1 text-xs text-zinc-500">
                                    Enhanced scoring: additive rule contribution with asymmetric dampening for repeated same-direction
                                    signals
                                </div>
                            </div>
                            <ExpandableDataTable
                                allowOverflow
                                search
                                sort
                                striped
                                columns={[
                                    { header: 'Date/Time', accessorKey: 'dateTime', size: 200 },
                                    {
                                        header: 'Decision',
                                        accessorKey: 'decision',
                                        size: 110,
                                        cell: ({ row }) => (
                                            <span className={row.original.decision === 'accept' ? 'text-green-400' : 'text-red-400'}>
                                                {row.original.decision ?? '—'}
                                            </span>
                                        )
                                    },
                                    {
                                        id: 'predictedProfit',
                                        header: 'Predicted Profit',
                                        accessorFn: (row) => getNullableNumberSortValue(row.predictedProfit),
                                        sortDescFirst: true,
                                        sortFn: 'basic',
                                        sortUndefined: 'last',
                                        size: 140,
                                        cell: ({ row }) =>
                                            row.original.predictedProfit != null
                                                ? `${row.original.predictedProfit >= 0 ? '+' : ''}${Number(row.original.predictedProfit).toFixed(2)}%`
                                                : '—'
                                    },
                                    {
                                        header: 'Baseline',
                                        accessorKey: 'baselineReturn',
                                        size: 110,
                                        cell: ({ row }) =>
                                            row.original.baselineReturn != null ? `${Number(row.original.baselineReturn).toFixed(2)}%` : '—'
                                    },
                                    {
                                        header: 'Baseline Status',
                                        accessorKey: 'entityNotInExperiment',
                                        size: 145,
                                        cell: ({ row }) =>
                                            row.original.entityNotInExperiment ? (
                                                <span className="text-amber-300">No eligible baseline</span>
                                            ) : (
                                                <span className="text-zinc-400">Eligible</span>
                                            )
                                    },
                                    {
                                        header: 'Window',
                                        accessorKey: 'beyondTrainingCutoff',
                                        size: 120,
                                        cell: ({ row }) =>
                                            row.original.beyondTrainingCutoff ? (
                                                <span className="text-amber-300">OOS</span>
                                            ) : (
                                                <span className="text-zinc-400">Training</span>
                                            )
                                    },
                                    {
                                        header: 'Contribution',
                                        accessorKey: 'adjustmentContribution',
                                        size: 110,
                                        cell: ({ row }) => {
                                            const contribution =
                                                row.original.adjustmentContribution ??
                                                (row.original.predictedProfit != null && row.original.baselineReturn != null
                                                    ? row.original.predictedProfit - row.original.baselineReturn
                                                    : null);

                                            return contribution != null
                                                ? `${contribution >= 0 ? '+' : ''}${Number(contribution).toFixed(2)}pp`
                                                : '—';
                                        }
                                    },
                                    {
                                        header: 'Matched',
                                        accessorKey: 'matchedRules',
                                        size: 150,
                                        cell: ({ row }) => {
                                            if (row.original.matchedRules == null) return '—';

                                            const roleCounts = getPreviewRuleRoleCounts(row.original.ruleEvaluations);

                                            return (
                                                <div className="leading-tight">
                                                    <div>{row.original.matchedRules} total</div>
                                                    <div className="text-[11px] text-zinc-400">
                                                        {roleCounts.boost} boost / {roleCounts.penalty} penalty / {roleCounts.veto} veto
                                                    </div>
                                                </div>
                                            );
                                        }
                                    },
                                    {
                                        header: 'Confidence',
                                        accessorKey: 'confidence',
                                        size: 110,
                                        cell: ({ row }) =>
                                            row.original.confidence != null ? `${(Number(row.original.confidence) * 100).toFixed(0)}%` : '—'
                                    }
                                ]}
                                data={previewRows}
                                pagination={{ enabled: previewRows.length > 10, pageSize: 10 }}
                                renderSubComponent={({ row }) => (
                                    <PreviewEntryDetails registryFeatureByKey={registryFeatureByKey} row={row.original} />
                                )}
                            />
                        </div>
                    ) : (
                        !previewPending && (
                            <div className="text-zinc-500 text-sm">
                                {previewedEntityLabel
                                    ? `No entries matched the preview window for ${previewedEntityLabel}.`
                                    : 'Run a preview to see results.'}
                            </div>
                        )
                    )}
                </div>
            </div>
        </Card>
    );
};

export default PreviewCard;
